import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTheme } from "../context/useThemeContext";
import useMainStore from "../stores/mainStore";
import { saveMainStore } from "../utility/cache/manageMainStore";
import useDataStore from "../stores/dataStore";
import useAccountManagementStore from "../stores/accountManagementStore";

function HorizontalResizableLayout(props: {
    isCollapsed?: boolean;
    leftContent: React.ReactNode;
    rightContent: React.ReactNode;
    leftSidebarIsOverlay?: boolean;
}) {
    const { leftContent, rightContent } = props;
    const leftContentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const clickedInRightContent = (event.target as Element).closest(
                ".rightContent"
            );

            if (props.leftSidebarIsOverlay && clickedInRightContent) {
                useMainStore.getState().set.leftSidebarCollapsed(() => true);
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [props.leftSidebarIsOverlay]);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                height: "100%",
                overflow: "hidden",
            }}
        >
            <div
                ref={leftContentRef}
                style={{
                    position: props.leftSidebarIsOverlay ? "absolute" : "unset",
                    boxShadow: props.leftSidebarIsOverlay
                        ? "0 0 10px rgba(0, 0, 0, 0.5)"
                        : "none",
                    zIndex: 99,
                    left: 0,
                    top: 0,
                    bottom: 0,
                }}
            >
                <LeftContent
                    content={leftContent}
                    minWidth={250}
                    maxWidth={750}
                    isCollapsed={props.isCollapsed}
                />
            </div>
            {/* Right Content */}
            <div
                className="rightContent"
                style={{
                    flex: 1,
                    height: "100%",
                    overflow: "hidden",
                }}
            >
                {rightContent}
            </div>
        </div>
    );
}

function LeftContent(props: {
    content: React.ReactNode;
    minWidth: number;
    maxWidth: number;
    isCollapsed?: boolean;
}) {
    // Store
    const isLoaded = useMainStore((state) => state.data.loaded);
    const leftSidebarWidth = useMainStore(
        (state) => state.data.leftSidebarWidth
    );

    // State
    const [currentLeftWidth, setCurrentLeftWidth] = useState(leftSidebarWidth); //? Need to set separately from lelftSidebarWidth for transition to work
    const [isTransitioning, setIsTransitioning] = useState(false); // True when the left content is animating to a new width

    useEffect(() => {
        setCurrentLeftWidth(leftSidebarWidth);
    }, [isLoaded]);

    /**
     * Handles the resize event of the horizontal resizer.
     * @param {number} offsetWidth - The width of the left content in pixels.
     */
    function handleResize(offsetWidth: number) {
        // setCurrentLeftWidth(offsetWidth);

        // Set size in store
        const newWidth = Math.max(
            props.minWidth,
            Math.min(offsetWidth, props.maxWidth)
        );

        setCurrentLeftWidth(newWidth);
        useMainStore.getState().set.setLeftSidebarWidth(() => newWidth);
    }

    // useEffect(() => {
    //     if (isLoaded) {
    //         setCurrentLeftWidth(leftSidebarWidth);
    //     }
    // }, [isLoaded]);

    // useEffect
    // Allow smooth transition on collapse, while not interfering with user resize of left content
    useEffect(() => {
        setIsTransitioning(true);

        // Collapse Width
        if (props.isCollapsed) {
            setCurrentLeftWidth(0);
        } else {
            setCurrentLeftWidth(leftSidebarWidth);
        }

        // Need to disable/enable transition to not interfere with user resize
        const timer = setTimeout(() => {
            setIsTransitioning(false);
        }, 250);

        return () => clearTimeout(timer);
    }, [props.isCollapsed]);

    // Content won't rerender, since it's passed as a prop
    return (
        <div
            className="leftContent relative"
            style={{
                width: `${currentLeftWidth}px`,
                height: "100%",
                overflow: "hidden",
                maxWidth: props.maxWidth,
                transition: isTransitioning
                    ? "width 0.25s ease-in-out"
                    : "none",
                pointerEvents: props.isCollapsed ? "none" : "unset",
            }}
        >
            <ResizeHandler
                onResize={handleResize}
                setCurrentLeftWidth={setCurrentLeftWidth}
            />
            {props.content}
        </div>
    );
}
const ResizeHandleStyled = styled.div<{
    $color: string;
    $colorOverride?: string;
}>`
    z-index: 999;
    position: absolute;
    right: 0px;
    .innerHandle {
        width: 3px;
        height: 100%;
        margin-left: auto;
        background-color: ${({ $colorOverride }) => $colorOverride || null};
    }
    &:hover {
        .innerHandle {
            background-color: ${({ $color }) => $color};
            transition-delay: 0.1s;
        }
    }
    width: 6px;
    height: 100%;
    cursor: ew-resize;
    transition: all 0.05s ease-in-out;
`;

function ResizeHandler(props: {
    setCurrentLeftWidth: (width: number) => void;
    onResize?: (width: number) => void;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const { theme } = useTheme();

    const dragStartXRef = useRef(0);
    const [isDragging, setIsDragging] = useState(false);

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        dragStartXRef.current = e.clientX;

        document.body.classList.add("isDragging-Horizontal");

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };

    const handleMouseMove = (e: MouseEvent) => {
        const clientX = e.clientX;
        props.onResize?.(clientX);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        dragStartXRef.current = 0;

        document.body.classList.remove("isDragging-Horizontal");

        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);

        // Save Changes
        const userID = useAccountManagementStore.getState().userData?.id;
        saveMainStore(userID);
    };

    return (
        <ResizeHandleStyled
            ref={ref}
            onDragStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onDoubleClick={(e) => {
                e.preventDefault();
                // console.log("DOUBLE CLICK");
                // useMainStore.getState().set.setLeftSidebarWidth(() => 350);
                props.setCurrentLeftWidth(350);
                useMainStore.getState().set.setLeftSidebarWidth(() => 350);
            }}
            draggable={false}
            className="resizer"
            onMouseDown={handleMouseDown}
            $colorOverride={isDragging ? theme.accentColor : undefined}
            $color={theme.accentColor}
        >
            <div className="innerHandle"></div>
        </ResizeHandleStyled>
    );
}

export default HorizontalResizableLayout;
